import PropTypes from 'prop-types';
import React from 'react';
import get from 'lodash/get';
import {
  Button,
  Checkbox,
  SingleSelectionGroup,
  TextInput,
  BodyText,
} from '@paypalcorp/pp-react';

import * as analytics from '../lib/analytics';
import parseAmount from '../lib/parseAmount';
import { isPPGF } from '../lib/charity';
import classNames from 'classnames';
import styles from './donateBox.less';
import { DataContext } from '../lib/context';
import { TrackingImpressionPixel } from '../components/TrackingImpressionPixel';

export default class extends React.Component {
  static contextType = DataContext;
  static contextTypes = {
    csrf: PropTypes.string,
  };

  static defaultProps = {
    active: true,
  };

  constructor(props) {
    super(props);
    let nonanonymous = true;
    if (
      get(props, 'charity.address.country_code') === 'GB' ||
      props.isGroupDonation
    ) {
      nonanonymous = false;
    }
    if (props.amounts.tracking) {
      this.tracking = props.amounts.tracking.impression_url;
    }

    this.state = {
      nonanonymous,
      recurring: false,
      selectedAmount: '',
      clickedIndex: -1,
      inputAmount: '',
      radioBtnClicked: false,
      errorPopup: false,
      nemoTag: props.isGroupDonation ? 'gtg' : 'charity',
    };
  }

  handleNonAnonymousCheck = event => {
    this.setState(
      Object.assign({}, this.state, {
        nonanonymous: event.target.checked,
      })
    );
  };

  handleInputChange = event => {
    const value = event.target.value;
    let input = parseAmount(value, this.context.griffin.number);
    if (input === undefined) {
      return this.invalid();
    }

    this.setState({
      inputAmount: input,
    });
  };

  handleInputFocus = () => {
    this.setState({
      selectedAmount: '',
      radioBtnClicked: false,
      clickedIndex: -1,
    });
  };

  invalid = () => {
    this.setState({
      isInvalid: true,
    });

    setTimeout(() => {
      this.setState({
        isInvalid: false,
      });
    }, 2000);
  };

  handleDonateClick = (event, isGtg, npid) => {
    if (
      this.state.inputAmount === '' &&
      (!this.state.radioBtnClicked || this.state.selectedAmount === '')
    ) {
      event.preventDefault();
      this.invalid();
      this.setState({
        errorPopup: true,
      });
      setTimeout(() => {
        this.setState({
          errorPopup: false,
        });
      }, 2000);
    }

    analytics.trackLinkWithData(
      `${isGtg ? 'gtgdonateButton' : 'donateButton'}`,
      this.props.fptiInfo.pgrp,
      this.props.fptiInfo.page,
      {
        amount: this.state.selectedAmount || this.state.inputAmount,
        preset_y_n: this.state.selectedAmount !== '' ? 'y' : 'n',
        nonanonymous: this.state.nonanonymous,
        crid: npid,
      },
      'link'
    );
  };

  handleAmountSelection = (event, isGtg) => {
    if (this.props.amounts.tracking) {
      this.tracking = this.props.amounts.tracking.click_url;
    }
    this.setState({
      selectedAmount: event.target.value,
      radioBtnClicked: true,
      inputAmount: '',
    });
    analytics.trackLinkWithData(
      `${isGtg ? 'gtgAmtSelect' : 'donateAmtSelect'}`,
      this.props.fptiInfo.pgrp,
      this.props.fptiInfo.page,
      { amount: event.target.value },
      'link'
    );
  };

  handleKeyPressCheckBox(e) {
    if (e.key === 'Enter') {
      e.preventDefault();
      this.setState({
        nonanonymous: !this.state.nonanonymous,
      });
    }
  }

  donationForm = () => {
    const anonymousLabel = this.props.charityContent
      ? this.props.i18n(this.props.charityContent.anonymousCheck)
      : this.props.anonymousLabel;

    let nonAnonymousInput;

    if (
      isPPGF(this.props.charity.charity_type) &&
      !this.props.isGroupDonation
    ) {
      nonAnonymousInput = (
        <Checkbox
          checked={this.state.nonanonymous}
          label={anonymousLabel}
          className={styles.checkBox}
          onChange={this.handleNonAnonymousCheck}
          onKeyDown={e => this.handleKeyPressCheckBox(e)}
          name='nonanonymous-checkbox'
        />
      );
    }

    let name;
    if (isPPGF(this.props.charity.charity_type)) {
      name = this.props.i18n('label.ppgf-donation-description', {
        charity: this.props.isGroupDonation
          ? this.props.virtualCharity.name
          : this.props.charity.name,
      });
    } else {
      name = this.props.i18n('label.donation-description', {
        charity: this.props.isGroupDonation
          ? this.props.virtualCharity.name
          : this.props.charity.name,
      });
    }

    let charityNpid = this.props.isGroupDonation
      ? this.props.charity.groupVirtualNpid
      : this.props.charity.nonprofit_id;

    return (
      <form
        action={this.props.formUrl}
        method={'post'}
        className={styles.formStyle}
        id={`nemo_donation_form_${this.state.nemoTag}`}
      >
        <input
          type={'hidden'}
          name={'_csrf'}
          value={this.props.csrf || this.context.csrf}
        />
        <input type={'hidden'} name={'npid'} value={charityNpid} />
        <input
          type={'hidden'}
          name={'eventUrl'}
          value={this.props.eventUrl || 'eventUrl'}
        />
        <input type={'hidden'} name={'name'} value={name} />
        <input
          type={'hidden'}
          name={'nonanonymous'}
          value={this.state.nonanonymous}
        />
        <input
          type={'hidden'}
          name={'currency'}
          value={this.props.amounts.currencyCode}
        />
        <input
          type={'hidden'}
          name={'amount'}
          value={
            this.state.selectedAmount
              ? this.state.selectedAmount
              : this.state.inputAmount
          }
        />
        {nonAnonymousInput}
        <Button
          className={styles.donateButton}
          disabled={!this.props.enable}
          type={'submit'}
          onClick={e =>
            this.handleDonateClick(e, this.props.isGroupDonation, charityNpid)
          }
          id={`nemo_donatebutton_${this.state.nemoTag}`}
        >
          {this.props.i18n('label.donate-now')}
        </Button>
      </form>
    );
  };

  renderDonationError = () => {
    if (this.props.donationInfo && this.props.donationInfo.donated === 2) {
      return (
        <div className={'npo-chkout-error-msg'}>
          <div className={'iconStyling'}>
            <img
              src={`https://${this.props.cdnHost}/images/Cause/ic_critical.svg`}
            />
          </div>
          <div className={'errStyling'}>
            <BodyText as='p' size='md'>
              {this.props.i18n('label.error-msg')}
            </BodyText>
          </div>
          <br />
        </div>
      );
    }
  };

  render() {
    const inputBoxStyling = classNames(styles.textInputMargin, {
      showError: this.state.errorPopup,
    });
    const currencyCode = this.props.amounts.currencyCode;
    const otherAmountPlaceholder = `${this.props.i18n(
      'label.other-amount'
    )} (${currencyCode})`;

    const selectionOptions = this.props.amounts.options.map((option, index) => {
      return {
        label: option.formatted,
        value: option.value,
      };
    });

    // modal currently not used
    const modal = (
      <div>
        <div
          style={{
            width: '90%',
            margin: '0 auto',
          }}
          dangerouslySetInnerHTML={{
            __html: this.props.i18n('label.modal-content-donate'),
          }}
        />
      </div>
    );
    return (
      <div className={styles.donationSelection}>
        {this.tracking ? <TrackingImpressionPixel src={this.tracking} /> : null}
        <SingleSelectionGroup
          id={this.props.id}
          name={`amounts_${this.state.nemoTag}`}
          align='justify'
          responsive='sm'
          value={this.state.selectedAmount}
          onChange={e =>
            this.handleAmountSelection(e, this.props.isGroupDonation)
          }
          options={selectionOptions}
        />
        <TextInput
          name='help'
          type={'text'}
          id={`nemo_inputAmount_${this.state.nemoTag}`}
          errorText={
            this.state.errorPopup && this.props.i18n('label.input-error')
          }
          helperText={
            this.state.isInvalid && this.props.i18n('label.invalid-input')
          }
          className={`${inputBoxStyling}`}
          onChange={this.handleInputChange}
          onFocus={this.handleInputFocus}
          value={this.state.inputAmount}
          label={otherAmountPlaceholder}
          autoComplete={'off'}
          required
        />
        {this.donationForm()}
      </div>
    );
  }
}
